import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import Header from '@/components/layout/Header';
import { getImageUrl } from '@/utils/helpers';
import { 
  Search, Loader2, User, Users, Palette, Building2, 
  Sparkles, ChevronRight, UserPlus
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// 用户类型配置 - 使用翻译键
const USER_TYPE_CONFIG = {
  artist: {
    titleKey: 'userTypes.artist',
    descKey: 'userTypes.artistDesc',
    icon: Palette,
    gradient: 'from-purple-600 to-pink-500',
    lightGradient: 'from-purple-50 to-pink-50',
  },
  collector: {
    titleKey: 'userTypes.collector',
    descKey: 'userTypes.collectorDesc',
    icon: Sparkles,
    gradient: 'from-amber-500 to-orange-500',
    lightGradient: 'from-amber-50 to-orange-50',
  },
  museum: {
    titleKey: 'userTypes.museum',
    descKey: 'userTypes.museumDesc',
    icon: Building2,
    gradient: 'from-blue-600 to-cyan-500',
    lightGradient: 'from-blue-50 to-cyan-50',
  },
  curator: {
    titleKey: 'userTypes.curator',
    descKey: 'userTypes.curatorDesc',
    icon: Users,
    gradient: 'from-emerald-500 to-teal-500',
    lightGradient: 'from-emerald-50 to-teal-50',
  },
};

const UserTypeListPage = () => {
  const { userType } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user, token } = useAuth();
  
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [followingMap, setFollowingMap] = useState({});

  const config = USER_TYPE_CONFIG[userType] || USER_TYPE_CONFIG.artist;
  const IconComponent = config.icon;

  useEffect(() => {
    fetchUsers();
  }, [userType]);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/users/by-type/${userType}`);
      setUsers(res.data);
      
      // 如果用户已登录，获取关注状态
      if (token) {
        const followRes = await axios.get(`${API}/users/me/following`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const followingIds = new Set(followRes.data.map(u => u.id));
        const map = {};
        res.data.forEach(u => {
          map[u.id] = followingIds.has(u.id);
        });
        setFollowingMap(map);
      }
    } catch (err) {
      console.error('Failed to fetch users:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleFollow = async (userId, e) => {
    e.stopPropagation();
    if (!user) {
      navigate('/login');
      return;
    }
    try {
      const res = await axios.post(`${API}/users/${userId}/follow`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFollowingMap(prev => ({ ...prev, [userId]: res.data.following }));
      setUsers(prev => prev.map(u => 
        u.id === userId 
          ? { ...u, followers_count: (u.followers_count || 0) + (res.data.following ? 1 : -1) }
          : u
      ));
    } catch (err) {
      console.error('Failed to toggle follow:', err);
    }
  };

  const filteredUsers = users.filter(u => 
    (u.name?.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (u.name?.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (u.bio?.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  return (
    <>
      <Header />
      <div className="min-h-screen bg-amber-50 pt-16" data-testid="user-type-list-page">
        {/* Hero区域 */}
        <div className={`relative bg-gradient-to-br ${config.gradient} py-20 px-6`}>
          <div className="max-w-4xl mx-auto text-center text-white">
            <div className="w-20 h-20 mx-auto mb-6 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center">
              <IconComponent className="w-10 h-10" />
            </div>
            <h1 className="text-4xl md:text-5xl font-light mb-4">
              {t(config.titleKey)}
            </h1>
            <p className="text-lg text-white/80 mb-8">
              {t(config.descKey)}
            </p>
            
            {/* 搜索框 */}
            <div className="max-w-md mx-auto relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-amber-500" />
              <Input
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder={t('userTypes.searchPlaceholder', { type: t(config.titleKey) })}
                className="pl-12 py-6 rounded-full bg-white/90 backdrop-blur border-0 text-amber-900"
              />
            </div>
          </div>
        </div>

        {/* 用户列表 */}
        <div className="max-w-6xl mx-auto px-6 py-12">
          {loading ? (
            <div className="flex justify-center py-20">
              <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
            </div>
          ) : filteredUsers.length > 0 ? (
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {filteredUsers.map((u) => (
                <UserCard
                  key={u.id}
                  user={u}
                  userType={userType}
                  config={config}
                  isFollowing={followingMap[u.id]}
                  isOwnProfile={user?.id === u.id}
                  onFollow={handleFollow}
                  onClick={() => {
                    // 如果是创作者，跳转到创作者页面，否则跳转到用户主页
                    if (u.is_creator) {
                      navigate(`/creator/${u.id}`);
                    } else {
                      navigate(`/profile/${u.id}`);
                    }
                  }}
                />
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <div className={`w-20 h-20 mx-auto mb-6 bg-gradient-to-br ${config.lightGradient} rounded-full flex items-center justify-center`}>
                <IconComponent className="w-10 h-10 text-amber-500" />
              </div>
              <h3 className="text-xl font-medium text-amber-800 mb-2">
                {t('userTypes.noUsers', { type: t(config.titleKey) })}
              </h3>
              <p className="text-amber-600">
                {searchTerm ? t('common.noSearchResults') : t('userTypes.noUsersYet', { type: t(config.titleKey) })}
              </p>
            </div>
          )}
        </div>

        {/* 底部导航到其他类型 */}
        <div className="bg-white border-t py-12 px-6">
          <div className="max-w-4xl mx-auto">
            <h3 className="text-lg font-medium text-amber-900 mb-6 text-center">
              {t('userTypes.exploreMore')}
            </h3>
            <div className="flex flex-wrap justify-center gap-4">
              {Object.entries(USER_TYPE_CONFIG)
                .filter(([key]) => key !== userType)
                .map(([key, cfg]) => {
                  const Icon = cfg.icon;
                  return (
                    <Button
                      key={key}
                      variant="outline"
                      className="rounded-full px-6"
                      onClick={() => navigate(`/users/${key}`)}
                    >
                      <Icon className="w-4 h-4 mr-2" />
                      {t(cfg.titleKey)}
                      <ChevronRight className="w-4 h-4 ml-1" />
                    </Button>
                  );
                })}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

// 用户卡片组件 - 带作品展示
const UserCard = ({ user, userType, config, isFollowing, isOwnProfile, onFollow, onClick }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const displayName = user.name;
  const displayAvatar = user.avatar;
  const displayBio = user.bio;
  const latestArtworks = user.latest_artworks || [];
  
  // 点击作品
  const handleArtworkClick = (e, artworkId) => {
    e.stopPropagation();
    navigate(`/artwork/${artworkId}`);
  };
  
  return (
    <div
      className="bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-lg transition-all cursor-pointer group"
      onClick={onClick}
      data-testid={`user-card-${user.id}`}
    >
      {/* 顶部：头像和作品并排 */}
      <div className={`bg-gradient-to-br ${config.gradient} p-4`}>
        <div className="flex items-start gap-3">
          {/* 头像 */}
          <div className="flex-shrink-0">
            <div className="w-16 h-16 rounded-full bg-white p-[2px]">
              <div className="w-full h-full rounded-full overflow-hidden bg-amber-100">
                {displayAvatar ? (
                  <img loading="lazy"
                    src={getImageUrl(displayAvatar)}
                    alt={displayName}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <span className="text-xl font-medium text-amber-500">
                      {displayName?.charAt(0) || '?'}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>
          
          {/* 作品缩略图 - 头像右侧 */}
          <div className="flex-1 flex gap-1.5 overflow-hidden">
            {latestArtworks.length > 0 ? (
              latestArtworks.slice(0, 3).map((artwork, idx) => (
                <div
                  key={artwork.id || idx}
                  className="flex-1 aspect-square rounded-lg overflow-hidden bg-white/20 hover:ring-2 ring-white/50 transition-all"
                  onClick={(e) => handleArtworkClick(e, artwork.id)}
                  title={artwork.title}
                >
                  <img loading="lazy"
                    src={getImageUrl(artwork.image_url)}
                    alt={artwork.title}
                    className="w-full h-full object-cover"
                  />
                </div>
              ))
            ) : (
              // 没有作品时显示占位
              <div className="flex-1 flex items-center justify-center text-white/50 text-xs">
                {t('userTypes.noArtworks')}
              </div>
            )}
          </div>
        </div>
        
        {/* 创作者标签 - 已移除，因为发布作品的用户都是创作者 */}
      </div>

      {/* 信息区域 */}
      <div className="px-4 py-4">
        <div className="flex items-start justify-between mb-2">
          <div>
            <h3 className="text-base font-medium text-amber-900 group-hover:text-purple-600 transition-colors">
              {displayName || t('common.unnamed')}
            </h3>
            {/* 显示user_title，但过滤掉可能是邮箱的内容 */}
            {user.user_title && !user.user_title.includes('@') && (
              <p className="text-xs text-amber-600">{user.user_title}</p>
            )}
          </div>
          
          {!isOwnProfile && (
            <Button
              size="sm"
              variant={isFollowing ? "outline" : "default"}
              className={`text-xs h-7 ${!isFollowing ? `bg-gradient-to-r ${config.gradient} border-0` : ''}`}
              onClick={(e) => onFollow(user.id, e)}
            >
              {isFollowing ? t('common.following') : <><UserPlus className="w-3 h-3 mr-1" />{t('common.follow')}</>}
            </Button>
          )}
        </div>

        {displayBio && (
          <p className="text-xs text-amber-700 line-clamp-2 mb-3">
            {displayBio}
          </p>
        )}

        {/* 统计数据 */}
        <div className="flex items-center gap-3 text-xs text-amber-600">
          <span><strong className="text-amber-900">{user.followers_count || 0}</strong> {t('common.followers')}</span>
          <span><strong className="text-amber-900">{user.artworks_count || 0}</strong> {t('common.works')}</span>
        </div>

        {/* 机构信息（美术馆/收藏家） */}
        {(userType === 'museum' || userType === 'collector') && user.institution_name && (
          <div className="mt-3 pt-3 border-t">
            <p className="text-xs text-amber-700">
              <Building2 className="w-3 h-3 inline mr-1" />
              {user.institution_name}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default UserTypeListPage;
